/**
 * Organization Locale Hook
 *
 * Provides organization locale settings and formatting utilities in a single hook.
 * Automatically fetches settings from organization context and provides memoized formatters.
 *
 * Usage:
 *   const { settings, formatters } = useOrganizationLocale();
 *   formatters.formatDate(new Date()); // Uses org settings automatically
 *   formatters.formatCurrency(1234.56); // Uses org currency & number format
 */

import { useMemo } from 'react';
import { useOrganization } from '@/lib/platform/contexts/organization-context';
import {
  formatDate,
  formatNumber,
  formatCurrency,
  formatTimestamp,
  formatRelativeTime,
  parseNumber,
  type DateFormatType,
  type NumberFormatType,
  type LanguageType,
} from '@/lib/platform/utils/locale-formatting';

/**
 * Organization locale settings
 */
export interface OrganizationLocaleSettings {
  language: LanguageType;
  dateFormat: DateFormatType;
  numberFormat: NumberFormatType;
  timezone: string;
  currency: string;
}

/**
 * Formatting functions with organization settings pre-applied
 */
export interface OrganizationFormatters {
  // Date formatting (uses org dateFormat + language)
  formatDate: (date: Date | string | null | undefined) => string;

  // Number formatting (uses org numberFormat)
  formatNumber: (value: number | null | undefined, decimals?: number) => string;

  // Currency formatting (uses org currency + numberFormat)
  formatCurrency: (value: number | null | undefined, decimals?: number) => string;

  // Timestamp formatting (uses org dateFormat + language + timezone)
  formatTimestamp: (date: Date | string | null | undefined, includeTime?: boolean) => string;

  // Relative time formatting (uses org language)
  formatRelativeTime: (date: Date | string | null | undefined) => string;

  // Parse formatted number back to number (uses org numberFormat)
  parseNumber: (value: string) => number | null;
}

/**
 * Hook return type
 */
export interface UseOrganizationLocaleReturn {
  settings: OrganizationLocaleSettings;
  formatters: OrganizationFormatters;
}

/**
 * Default settings (fallback if organization context not loaded)
 */
const DEFAULT_SETTINGS: OrganizationLocaleSettings = {
  language: 'en',
  dateFormat: 'DD-MM-YYYY',
  numberFormat: 'eu',
  timezone: 'Europe/Copenhagen',
  currency: 'DKK',
};

/**
 * Hook to get organization locale settings and formatters
 *
 * @returns Organization settings and pre-configured formatter functions
 *
 * @example
 * function MyComponent() {
 *   const { settings, formatters } = useOrganizationLocale();
 *
 *   return (
 *     <div>
 *       <p>Language: {settings.language}</p>
 *       <p>Date: {formatters.formatDate(new Date())}</p>
 *       <p>Amount: {formatters.formatCurrency(1234.56)}</p>
 *     </div>
 *   );
 * }
 */
export function useOrganizationLocale(): UseOrganizationLocaleReturn {
  const { organization } = useOrganization();

  // Extract locale settings from organization (with fallbacks)
  const settings: OrganizationLocaleSettings = useMemo(() => {
    if (!organization) {
      return DEFAULT_SETTINGS;
    }

    const extractedSettings = {
      language: (organization.default_language as LanguageType) || DEFAULT_SETTINGS.language,
      dateFormat:
        (organization.default_date_format as DateFormatType) || DEFAULT_SETTINGS.dateFormat,
      numberFormat:
        (organization.default_number_format as NumberFormatType) || DEFAULT_SETTINGS.numberFormat,
      timezone: organization.default_timezone || DEFAULT_SETTINGS.timezone,
      currency: organization.base_currency || DEFAULT_SETTINGS.currency,
    };

    return extractedSettings;
  }, [organization]);

  // Create memoized formatters with org settings pre-applied
  const formatters: OrganizationFormatters = useMemo(() => {
    return {
      formatDate: (date: Date | string | null | undefined) =>
        formatDate(date, settings.dateFormat, settings.language),

      formatNumber: (value: number | null | undefined, decimals: number = 2) =>
        formatNumber(value, settings.numberFormat, decimals),

      formatCurrency: (value: number | null | undefined, decimals: number = 0) =>
        formatCurrency(value, settings.currency, settings.numberFormat, decimals),

      formatTimestamp: (date: Date | string | null | undefined, includeTime: boolean = true) =>
        formatTimestamp(
          date,
          settings.dateFormat,
          settings.language,
          settings.timezone,
          includeTime
        ),

      formatRelativeTime: (date: Date | string | null | undefined) =>
        formatRelativeTime(date, settings.language),

      parseNumber: (value: string) => parseNumber(value, settings.numberFormat),
    };
  }, [settings]);

  return {
    settings,
    formatters,
  };
}

/**
 * Helper hook to just get formatters (if you don't need settings)
 *
 * @returns Pre-configured formatter functions
 *
 * @example
 * function MyComponent() {
 *   const { formatDate, formatCurrency } = useOrganizationFormatters();
 *
 *   return <p>{formatCurrency(1234.56)}</p>;
 * }
 */
export function useOrganizationFormatters(): OrganizationFormatters {
  const { formatters } = useOrganizationLocale();
  return formatters;
}

/**
 * Helper hook to just get settings (if you don't need formatters)
 *
 * @returns Organization locale settings
 *
 * @example
 * function MyComponent() {
 *   const { language, currency } = useOrganizationLocaleSettings();
 *
 *   return <p>Language: {language}</p>;
 * }
 */
export function useOrganizationLocaleSettings(): OrganizationLocaleSettings {
  const { settings } = useOrganizationLocale();
  return settings;
}
